import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { DAY_MIN, MIN_BLOCK_MIN, clampBlock, snap, type GridInterval } from './gridMath.js';
import type { TimeGridBlock, TimeGridColumn } from './TimeGrid.js';

/** Y→minute and X→column converters supplied by the grid (doc 10 §13). */
export interface Geometry {
  minFromClientY(clientY: number): number;
  columnIdFromClientX(clientX: number): string | null;
}

type Drag =
  | { mode: 'drawing'; columnId: string; anchorMin: number; curMin: number }
  | {
      mode: 'moving';
      blockId: string;
      columnId: string;
      startMin: number;
      endMin: number;
      grabMin: number;
      curMin: number;
      grabDeltaCol: string;
      moved: boolean;
    }
  | { mode: 'resizing'; blockId: string; columnId: string; edge: 'start' | 'end'; fixedMin: number; curMin: number }
  | null;

const PREVIEW_ID = '__preview__';
const MOVE_THRESHOLD_MIN = 3;

/**
 * The grid's pointer interaction machine (doc 10 §14): draw on empty space,
 * move a block body (incl. across days), resize an edge. One pointer-event code
 * path for mouse/touch/pen. Window listeners are used (not pointer capture) so a
 * cross-day move keeps tracking after the pointer leaves the origin column.
 */
export function useDragState(opts: {
  blocks: TimeGridBlock[];
  columns: TimeGridColumn[];
  geometry: Geometry;
  onCommit(next: TimeGridBlock[]): void;
  onSelect?(id: string | null): void;
  disabled: boolean;
}) {
  const { blocks, geometry, onCommit, onSelect, disabled } = opts;
  const dragRef = useRef<Drag>(null);
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  // Keep a live ref to the latest blocks/handlers for the window listeners.
  const state = useRef({ blocks, geometry, onCommit, onSelect });
  state.current = { blocks, geometry, onCommit, onSelect };

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const d = dragRef.current;
      if (!d) return;
      const { geometry: g } = state.current;
      const min = g.minFromClientY(e.clientY);
      if (d.mode === 'drawing') {
        d.curMin = min;
      } else if (d.mode === 'moving') {
        d.grabDeltaCol = g.columnIdFromClientX(e.clientX) ?? d.columnId; // tracked below
        d.curMin = min;
        if (Math.abs(min - d.grabMin) >= MOVE_THRESHOLD_MIN || d.grabDeltaCol !== d.columnId) d.moved = true;
      } else {
        d.curMin = min;
      }
      rerender();
    }
    function onUp() {
      commit();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dragRef.current) {
        dragRef.current = null;
        rerender();
      }
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function commit() {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    const { blocks: bs, onCommit: commitFn, onSelect: select } = state.current;

    if (d.mode === 'drawing') {
      const iv = clampBlock({ startMin: Math.min(d.anchorMin, d.curMin), endMin: Math.max(d.anchorMin, d.curMin) });
      if (iv.endMin - iv.startMin >= MIN_BLOCK_MIN) {
        commitFn([...bs, { id: PREVIEW_ID, columnId: d.columnId, kind: 'editable', ...iv }]);
      }
    } else if (d.mode === 'moving') {
      if (!d.moved) {
        select?.(d.blockId); // a click, not a drag → select + open popover
      } else {
        const moved = movedBlock(d);
        commitFn(bs.map((b) => (b.id === d.blockId ? { ...b, ...moved } : b)));
      }
    } else if (d.mode === 'resizing') {
      const iv = resizedBlock(d);
      commitFn(bs.map((b) => (b.id === d.blockId ? { ...b, ...iv } : b)));
    }
    rerender();
  }

  function movedBlock(d: Extract<Drag, { mode: 'moving' }>): { startMin: number; endMin: number; columnId: string } {
    const dur = d.endMin - d.startMin;
    let start = snap(d.startMin + (d.curMin - d.grabMin));
    start = Math.max(0, Math.min(DAY_MIN - dur, start));
    return { startMin: start, endMin: start + dur, columnId: d.grabDeltaCol };
  }

  function resizedBlock(d: Extract<Drag, { mode: 'resizing' }>): GridInterval {
    const raw =
      d.edge === 'start'
        ? { startMin: Math.min(d.curMin, d.fixedMin - MIN_BLOCK_MIN), endMin: d.fixedMin }
        : { startMin: d.fixedMin, endMin: Math.max(d.curMin, d.fixedMin + MIN_BLOCK_MIN) };
    return clampBlock(raw);
  }

  function onCanvasPointerDown(e: ReactPointerEvent, columnId: string) {
    if (disabled) return;
    const anchorMin = geometry.minFromClientY(e.clientY);
    dragRef.current = { mode: 'drawing', columnId, anchorMin, curMin: anchorMin };
    rerender();
  }

  function onBlockPointerDown(e: ReactPointerEvent, block: TimeGridBlock, edge: 'start' | 'end' | null) {
    if (disabled || block.kind !== 'editable') return;
    e.stopPropagation();
    const min = geometry.minFromClientY(e.clientY);
    if (edge) {
      dragRef.current = {
        mode: 'resizing',
        blockId: block.id,
        columnId: block.columnId,
        edge,
        fixedMin: edge === 'start' ? block.endMin : block.startMin,
        curMin: min,
      };
    } else {
      dragRef.current = {
        mode: 'moving',
        blockId: block.id,
        columnId: block.columnId,
        startMin: block.startMin,
        endMin: block.endMin,
        grabMin: min,
        curMin: min,
        grabDeltaCol: block.columnId,
        moved: false,
      };
    }
    rerender();
  }

  const d = dragRef.current;
  let preview: TimeGridBlock | null = null;
  let hiddenId: string | null = null;
  if (d?.mode === 'drawing') {
    preview = {
      id: PREVIEW_ID,
      columnId: d.columnId,
      kind: 'editable',
      startMin: Math.min(d.anchorMin, d.curMin),
      endMin: Math.max(d.anchorMin, d.curMin),
    };
  } else if (d?.mode === 'moving' && d.moved) {
    hiddenId = d.blockId;
    preview = { id: PREVIEW_ID, kind: 'editable', ...movedBlock(d) };
  } else if (d?.mode === 'resizing') {
    hiddenId = d.blockId;
    preview = { id: PREVIEW_ID, columnId: d.columnId, kind: 'editable', ...resizedBlock(d) };
  }

  return { preview, hiddenId, onCanvasPointerDown, onBlockPointerDown };
}
